import React, { createContext, useContext, useEffect, useState } from 'react';
const ThemeContext = createContext();

const VALID_THEMES = [
    'dark', 'light', 'landing',
    'crimson_empire', 'teal_garden', 'rose_clay', 'sage_slate',
    'sunset_fire', 'neon_dream', 'ember_night', 'ocean_breeze',
    'berry_blast', 'cosmic_night'
];

export const ThemeProvider = ({ children }) => {
    const getDefault = () => {
        const saved = localStorage.getItem('dagenda-color-mode');
        if (saved && VALID_THEMES.includes(saved)) return saved;
        if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light';
        return 'dark';
    };

    const [colorMode, setColorModeState] = useState(getDefault);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', colorMode);
        localStorage.setItem('dagenda-color-mode', colorMode);
    }, [colorMode]);

    // Follow system preference only if on dark/light modes
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: light)');
        const handle = (e) => {
            const saved = localStorage.getItem('dagenda-color-mode');
            if (!saved || saved === 'dark' || saved === 'light') {
                setColorModeState(e.matches ? 'light' : 'dark');
            }
        };
        mq.addEventListener?.('change', handle) ?? mq.addListener?.(handle);
        return () => mq.removeEventListener?.('change', handle) ?? mq.removeListener?.(handle);
    }, []);

    const setMode = (mode) => {
        if (VALID_THEMES.includes(mode)) setColorModeState(mode);
    };

    const toggleColorMode = () =>
        setColorModeState(prev => prev === 'light' ? 'dark' : 'light');

    const isDark = colorMode === 'dark' || ['crimson_empire', 'neon_dream', 'ember_night', 'cosmic_night'].includes(colorMode);

    return (
        <ThemeContext.Provider value={{
            colorMode,
            theme: colorMode,
            dashboardTheme: colorMode,
            isDark,
            isLight: !isDark,
            shellTheme: isDark ? 'dark' : 'light',
            setColorMode: setMode,
            changeTheme: setMode,
            changeDashboardTheme: setMode,
            toggleColorMode,
            toggleTheme: toggleColorMode,
            validThemes: VALID_THEMES,
        }}>
            {children}
        </ThemeContext.Provider>
    );
};
export const useTheme = () => useContext(ThemeContext);
